import { useState } from "react";
import { ArchiveRestore, PauseCircle, UserRoundX, X } from "lucide-react";
import { EditorModal } from "@/components/EditorModal";
import { api, formatApiError } from "@/lib/api";

function formatStamp(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export const ProviderStatusDialog = ({ provider, onClose, onChanged }) => {
  const [status, setStatus] = useState("On leave");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await api.post(`/providers/${provider.id}/status`, { status, note });
      onChanged(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setSaving(false);
    }
  }

  return (
    <EditorModal onClose={onClose} testId="provider-status-modal">
      <section className="inline-edit-panel" data-testid="provider-status-dialog">
        <div className="section-heading">
          <div>
            <h2>Change status for {provider.first_name} {provider.last_name}</h2>
            <p>Their active requests and assignments move to the board's Archived view, where they can be restored. Portal sign-in is paused until they return to active. This action is logged.</p>
          </div>
          <button className="projection-icon-button" data-testid="close-provider-status" type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <form className="inline-edit-form" onSubmit={submit}>
          <fieldset className="provider-status-options inline-edit-full" data-testid="provider-status-options">
            <legend>New status</legend>
            <label><input checked={status === "On leave"} data-testid="provider-status-on-leave" name="provider-status" type="radio" value="On leave" onChange={() => setStatus("On leave")} /> On leave <small>Temporary absence, expected to return</small></label>
            <label><input checked={status === "Inactive"} data-testid="provider-status-inactive" name="provider-status" type="radio" value="Inactive" onChange={() => setStatus("Inactive")} /> Inactive <small>No longer available for placements</small></label>
          </fieldset>
          <label className="inline-edit-full">
            Note (optional)
            <input data-testid="provider-status-note-input" maxLength={300} placeholder="e.g. On leave for graduate school" type="text" value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          {error && <p className="form-message error-message" data-testid="provider-status-error">{error}</p>}
          <div className="board-confirm-actions">
            <button className="button button-secondary" data-testid="provider-status-cancel" type="button" onClick={onClose}>Cancel</button>
            <button className="button button-primary" data-testid="provider-status-submit" disabled={saving} type="submit"><PauseCircle size={14} /> {saving ? "Saving…" : `Mark ${status.toLowerCase()}`}</button>
          </div>
        </form>
      </section>
    </EditorModal>
  );
};

export const InactiveProvidersPanel = ({ providers, onReactivated, onError }) => {
  const [busyId, setBusyId] = useState("");
  if (!providers.length) return null;

  async function reactivate(provider) {
    setBusyId(provider.id);
    try {
      await api.post(`/providers/${provider.id}/status`, { status: "Approved" });
      await onReactivated(provider);
    } catch (requestError) {
      onError(formatApiError(requestError));
    } finally {
      setBusyId("");
    }
  }

  return (
    <section className="requests-table-panel provider-inactive-panel" data-testid="inactive-providers-panel">
      <div className="section-heading">
        <div>
          <h2>On leave & inactive providers</h2>
          <p>{providers.length} paused network account{providers.length === 1 ? "" : "s"} · archived work can be restored from the Master Staffing Board</p>
        </div>
      </div>
      <ul className="provider-inactive-list">
        {providers.map((provider) => (
          <li key={provider.id} data-testid={`inactive-provider-${provider.id}`}>
            <UserRoundX size={16} />
            <div>
              <strong>{provider.first_name} {provider.last_name}</strong>
              <span>
                <em className={`status-badge provider-status-badge provider-status-${provider.approval_status.toLowerCase().replace(/\s+/g, "-")}`} data-testid={`inactive-provider-status-${provider.id}`}>{provider.approval_status}</em>
                {provider.status_changed_at ? ` since ${formatStamp(provider.status_changed_at)}` : ""}{provider.status_note ? ` · ${provider.status_note}` : ""}
              </span>
            </div>
            <button className="button button-secondary" data-testid={`reactivate-provider-${provider.id}`} disabled={busyId === provider.id} type="button" onClick={() => reactivate(provider)}>
              <ArchiveRestore size={14} /> {busyId === provider.id ? "Restoring…" : "Return to active"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
